import React from "react";
import { Box, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useTemplateFileUpload } from "./useTemplateFileUpload";
import BaseModal from "../../../../components/Modal/BaseModal";

interface TemplateFileUploadProps {
  open: boolean;
  templateId: string;
  onClose: () => void;
}

export const TemplateFileUpload: React.FC<TemplateFileUploadProps> = ({
  open,
  templateId,
  onClose,
}) => {
  const {
    selectedFile,
    dragOver,
    fileInputRef,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    handleUpload,
    handleClose,
    formatFileSize,
    isSubmitDisabled,
    loading,
  } = useTemplateFileUpload({ templateId, onClose });

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Upload Template File"
      maxWidth="sm"
      fullWidth
      cancelText="Cancel"
      submitText="Upload"
      onSubmit={handleUpload}
      submitDisabled={isSubmitDisabled}
      submitLoading={loading}
      dataTestId="template-file-upload-modal"
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileSelect}
      />

      <Box
        sx={{
          border: 2,
          borderStyle: "dashed",
          borderColor: dragOver ? "primary.main" : "divider",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: dragOver ? "primary.light" : "transparent",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {selectedFile ? (
          <Box>
            <InsertDriveFileIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={500}>
              {selectedFile.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatFileSize(selectedFile.size)}
            </Typography>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary">
              Drag and drop a file here
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse
            </Typography>
          </Box>
        )}
      </Box>
    </BaseModal>
  );
};
